import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import TextAreaInput from "components/ui/input/TextAreaInput";
import toast from "components/ui/toast/Toast";
import { AnswerMarkdown } from "lib/core_components/ai/AnswerMarkdown";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import { useUpdateLeads } from "lib/hooks/api/leads/useUpdateLeads";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { AiContextProvider } from "lib/providers/AiContextProvider";
import { toLeadInlineSources } from "./toLeadInlineSources";

const SUMMARY_PLACEHOLDER = "Add a summary or note to this lead.";

// The lead summary is stored in the lead `description` column. Keep in sync with the
// max length enforced on the server update path (lead.model.ts).
const SUMMARY_MAX_LENGTH = 10000;

type Props = {
  lead: LeadDetails;
};

/**
 * Free-text "Lead summary" area shown on every lead. Leads with content render a read-only
 * view with clickable inline `[N]` citations (via AnswerMarkdown); clicking the panel switches
 * to a plain textarea for editing and persists on blur. Empty leads open straight into edit mode.
 * Sources are populated/cleared server-side (see lead.resolver) — the FE only sends description.
 */
export default function LeadSummary({ lead }: Props) {
  const { control, getValues, reset, formState } = useForm<{ description: string }>({
    defaultValues: { description: lead.description ?? "" },
  });

  // Empty leads open in edit mode (placeholder textarea); leads with content render the
  // citation view until the user clicks to edit.
  const hasContent = !!lead.description?.trim();
  const [isEditing, setIsEditing] = useState(!hasContent);

  // Keep the field in sync when the lead is refetched / updated elsewhere (e.g. cache update).
  useEffect(() => {
    reset({ description: lead.description ?? "" });
  }, [lead.description, reset]);

  const sources = useMemo(
    () => toLeadInlineSources(lead.descriptionSources ?? []),
    [lead.descriptionSources],
  );

  const { mutate: updateLeads } = useUpdateLeads({
    onSuccess: () => {
      toast.success("Lead summary updated");
      reset({}, { keepValues: true });
    },
    onError: (error) => {
      console.warn("Failed to update lead summary", JSON.stringify(error));
      toast.error("Failed to update lead summary");
    },
  });

  const submitSummaryUpdate = () => {
    setIsEditing(false);
    // Only persist when the user actually changed the summary.
    if (!formState.dirtyFields.description) return;
    updateLeads({
      updateLeadsRequest: {
        ids: [lead.id],
        updateData: { description: getValues("description") },
      },
    });
  };

  const showView = hasContent && !isEditing;

  return (
    <Panel
      column
      gap={16}
      aria-label="Lead summary"
      onClick={() => {
        if (!isEditing) setIsEditing(true);
      }}
    >
      <LabelRow>
        <Text fontWeight="500" color="sysTextSecondary">
          Lead summary
        </Text>
        <EditIcon className={EDIT_ICON_CLASS} aria-hidden>
          <UIcon icon="edit02" size={16} color="sysTextSecondary" />
        </EditIcon>
      </LabelRow>
      {showView ? (
        // AnswerMarkdown reads useAiContext() and throws without a provider — wrap it.
        <AiContextProvider
          context={{ resourceId: lead.id, resourceName: lead.name, resourceType: "LEAD_SUMMARY" }}
        >
          <AnswerMarkdown answer={lead.description ?? ""} sources={sources} previewInternalLinks />
        </AiContextProvider>
      ) : (
        <TextAreaInput
          name="description"
          control={control}
          placeholder={SUMMARY_PLACEHOLDER}
          maxLength={SUMMARY_MAX_LENGTH}
          minRows={1}
          borderless
          // Focus only when switching into edit from the view (not on initial empty-lead render).
          autoFocus={isEditing && hasContent}
          onBlurCapture={submitSummaryUpdate}
          aria-label="Lead summary input"
        />
      )}
    </Panel>
  );
}

// grey700 (#263247) with 4% / 8% alpha — matches the Figma dark-opacity tokens.
const PANEL_BG_DEFAULT = "rgba(38, 50, 71, 0.04)";
const PANEL_BG_HOVER = "rgba(38, 50, 71, 0.08)";

const EDIT_ICON_CLASS = "lead-summary-edit-icon";

const Panel = styled(Flex)(({ theme }) => ({
  backgroundColor: PANEL_BG_DEFAULT,
  padding: 20,
  borderRadius: 8,
  cursor: "text",
  transition: "background-color 0.15s ease",

  // Hover-filled / hover-empty: darker bg + show the pencil affordance.
  "&:hover:not(:focus-within)": {
    backgroundColor: PANEL_BG_HOVER,
    [`.${EDIT_ICON_CLASS}`]: { opacity: 1 },
  },

  // Strip the textarea's own chrome so the panel itself is the visible surface.
  "& textarea, & textarea:hover, & textarea:focus": {
    backgroundColor: "transparent",
    padding: 0,
    border: "none",
    outline: "none",
    boxShadow: "none",
    color: theme.colors.sysTextDefault,
  },
  "& textarea::placeholder": {
    color: theme.colors.sysTextPlaceholder,
  },
}));

const LabelRow = styled(Flex)({
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  width: "100%",
});

const EditIcon = styled.span({
  display: "inline-flex",
  opacity: 0,
  transition: "opacity 0.15s ease",
});
